import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const MINIMUM_PASSWORD_LENGTH = 8;

export function AuthPage() {
  const { login, register, user } = useAuth();
  const [mode, setMode] = useState("login");
  const [publicOwnerRegistrationEnabled, setPublicOwnerRegistrationEnabled] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "owner",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    let active = true;

    api.get("/auth/registration-status")
      .then((response) => {
        if (active) {
          setPublicOwnerRegistrationEnabled(response?.data?.enabled === true);
        }
      })
      .catch(() => {
        if (active) {
          setPublicOwnerRegistrationEnabled(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const handleChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        if (form.password.length < MINIMUM_PASSWORD_LENGTH) {
          setError(`La password deve contenere almeno ${MINIMUM_PASSWORD_LENGTH} caratteri.`);
          return;
        }
        await register({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
        });
      }
    } catch (err) {
      setError(err?.userMessage || "Autenticazione fallita. Controlla i tuoi dati.");
    }
  };

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-screen grid grid-cols-1 md:grid-cols-[1.2fr_1fr] outline-none"
      data-testid="auth-page"
    >
      <div className="hidden md:flex flex-col justify-between bg-slate-900 text-white p-12">
        <div>
          <h1 className="text-4xl font-bold section-title" data-testid="auth-hero-title">
            FullBook
          </h1>
          <p className="text-sm text-slate-300 mt-4" data-testid="auth-hero-subtitle">
            Recupera le cancellazioni, proteggi i ricavi e mantieni il team al completo.
          </p>
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400" data-testid="auth-hero-footer">
          Pianificazione intelligente per attività su appuntamento
        </p>
      </div>
      <div className="flex items-center justify-center bg-white px-5 py-8 sm:p-10">
        <div className="w-full max-w-md" data-testid="auth-form-container">
          <div className="mb-8 text-center md:hidden" data-testid="auth-mobile-branding">
            <p className="section-title text-4xl font-bold tracking-tight text-slate-900 drop-shadow-sm" data-testid="auth-mobile-brand">
              FullBook
            </p>
            <h1 className="mt-3 text-xl font-semibold section-title text-slate-950">
              Agenda orientata ai ricavi
            </h1>
            <p className="mt-2 text-sm text-slate-600">Accedi alla tua attività</p>
          </div>
          <div className="flex gap-2 mb-6" data-testid="auth-mode-toggle">
            <button
              className={`flex-1 px-4 py-2 rounded-sm border text-sm font-medium ${
                mode === "login"
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
              onClick={() => setMode("login")}
              data-testid="auth-mode-login-button"
            >
              Accesso
            </button>
            {publicOwnerRegistrationEnabled && (
              <button
                className={`flex-1 px-4 py-2 rounded-sm border text-sm font-medium ${
                  mode === "register"
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200"
                }`}
                onClick={() => setMode("register")}
                data-testid="auth-mode-register-button"
              >
                Registra la tua attività
              </button>
            )}
          </div>

          <form className="space-y-4" onSubmit={handleSubmit} data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label htmlFor="auth-name" className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="auth-name-label">
                  Nome completo
                </label>
                <input
                  id="auth-name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full mt-2 border border-slate-200 rounded-sm h-10 px-3 placeholder:text-slate-600"
                  placeholder="Giulia Rossi"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label htmlFor="auth-email" className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="auth-email-label">
                Email
              </label>
              <input
                id="auth-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className="w-full mt-2 border border-slate-200 rounded-sm h-10 px-3 placeholder:text-slate-600"
                placeholder="titolare@studio.com"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label htmlFor="auth-password" className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="auth-password-label">
                Password
              </label>
              <input
                id="auth-password"
                name="password"
                type="password"
                minLength={mode === "register" ? MINIMUM_PASSWORD_LENGTH : undefined}
                value={form.password}
                onChange={handleChange}
                className="w-full mt-2 border border-slate-200 rounded-sm h-10 px-3 placeholder:text-slate-600"
                placeholder="••••••••"
                data-testid="auth-password-input"
              />
            </div>
            {mode === "login" && (
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-slate-700 underline underline-offset-4"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </Link>
              </div>
            )}
            {mode === "register" && (
              <div>
                <label htmlFor="auth-role" className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="auth-role-label">
                  Ruolo attività
                </label>
                <select
                  id="auth-role"
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  className="w-full mt-2 border border-slate-200 rounded-sm h-10 px-3"
                  data-testid="auth-role-select"
                >
                  <option value="owner">Titolare</option>
                </select>
              </div>
            )}
            {error && (
              <div className="text-sm text-red-600" role="alert" data-testid="auth-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="w-full bg-slate-900 text-white rounded-sm h-11 font-medium transition-transform active:scale-95"
              data-testid="auth-submit-button"
            >
              {mode === "login" ? "Accedi" : "Crea account"}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

